import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import { api as axios } from '../../../api';
import url from '../../../serverUrl';
import { fmtPrice, priceDecimals } from './MarketStatsBar';
import './proTrading.css';
import ChartDrawings from './pro/ChartDrawings';

const INTERVAL_SEC = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 };

// ---------------------------------------------------------------------------
// Candlestick chart for the trade screen (TradingView Lightweight Charts).
//
// Candles + volume histogram from /hootdex/candles (OHLCV built from settled
// Hootdex fills; falls back to the published price index when a token hasn't
// traded). Interval buttons, crosshair OHLC legend, optional moving averages,
// live refresh every few seconds without resetting the user's zoom.
// ---------------------------------------------------------------------------

const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];
const REFRESH_MS = { '1m': 5000, '5m': 5000, '15m': 10000, '1h': 15000, '4h': 30000, '1d': 60000 };
const MAS = [
  { len: 7, color: '#f4a932' },
  { len: 25, color: '#c27cf6' },
  { len: 99, color: '#4f8ff0' }
];

// Hyperliquid-style palette (matches hlTheme.css).
const C = {
  bg: '#0f1a1f',
  grid: 'rgba(255,255,255,0.035)',
  text: '#949e9c',
  up: '#50d2c1',
  down: '#ed7088',
  upVol: 'rgba(80,210,193,0.4)',
  downVol: 'rgba(237,112,136,0.4)'
};

const sma = (candles, len) => {
  const out = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= len) sum -= candles[i - len].close;
    if (i >= len - 1) out.push({ time: candles[i].time, value: sum / len });
  }
  return out;
};

const fmtVol = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
  return n.toFixed(n >= 1 ? 2 : 4);
};

export default function ProChart({ symbol, chartSymbol }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const candlesRef = useRef([]);
  const [interval, setIntervalKey] = useState('1h');
  const [showMa, setShowMa] = useState(true);
  const [legend, setLegend] = useState(null);
  const [source, setSource] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [lastCandle, setLastCandle] = useState(null);
  const showMaRef = useRef(showMa);
  const [api, setApi] = useState(null); // { chart, candles } once created, for the drawing layer

  // Create the chart once.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { color: C.bg },
        textColor: C.text,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: 11,
        // Lightweight Charts licence: keep TradingView attribution visible.
        attributionLogo: true
      },
      grid: {
        vertLines: { color: C.grid },
        horzLines: { color: C.grid }
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.08, bottom: 0.25 } },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6
      }
    });
    const candles = chart.addCandlestickSeries({
      upColor: C.up,
      downColor: C.down,
      borderUpColor: C.up,
      borderDownColor: C.down,
      wickUpColor: C.up,
      wickDownColor: C.down,
      priceLineVisible: true
    });
    const volume = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
      lastValueVisible: false,
      priceLineVisible: false
    });
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    const mas = MAS.map((m) =>
      chart.addLineSeries({
        color: m.color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      })
    );
    seriesRef.current = { candles, volume, mas };
    chartRef.current = chart;
    setApi({ chart, candles });

    chart.subscribeCrosshairMove((param) => {
      const c = param?.time ? param.seriesData.get(candles) : null;
      if (c) {
        const full = candlesRef.current.find((x) => x.time === param.time);
        setLegend({ ...c, volume: full?.volume, volumeUsd: full?.volumeUsd });
      } else {
        setLegend(null);
      }
    });

    return () => {
      setApi(null);
      chart.remove();
      chartRef.current = null;
    };
  }, []);

  // Load + refresh data.
  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    let timer;
    let first = true;
    let failures = 0;
    setStatus('loading');
    setLegend(null);

    const load = () =>
      axios
        .get(`${url}/hootdex/candles`, {
          params: { symbol, chartSymbol, interval },
          // Long intervals can take a while the first time a market is
          // opened (the server builds and caches its daily buckets).
          timeout: 45000
        })
        .then((res) => {
          if (cancelled || !chartRef.current) return;
          const data = Array.isArray(res.data?.candles) ? res.data.candles : [];
          const { candles, volume, mas } = seriesRef.current;
          candlesRef.current = data;
          setLastCandle(data[data.length - 1] || null);
          setSource(res.data?.source || null);
          if (data.length === 0) {
            candles.setData([]);
            volume.setData([]);
            mas.forEach((s) => s.setData([]));
            setStatus('empty');
            return;
          }
          const dec = priceDecimals(data[data.length - 1].close);
          candles.applyOptions({
            priceFormat: { type: 'price', precision: dec, minMove: Math.pow(10, -dec) }
          });
          candles.setData(data.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })));
          volume.setData(
            data.map((c) => ({
              time: c.time,
              value: c.volume,
              color: c.close >= c.open ? C.upVol : C.downVol
            }))
          );
          MAS.forEach((m, i) => mas[i].setData(showMaRef.current ? sma(data, m.len) : []));
          if (first) {
            chartRef.current.timeScale().fitContent();
            first = false;
          }
          failures = 0;
          setStatus('ready');
        })
        .catch(() => {
          failures += 1;
          // Keep showing "Loading" through a couple of slow/failed tries; only
          // call it unavailable after that (and keep retrying either way).
          if (!cancelled && first && failures >= 3) setStatus('error');
        })
        .finally(() => {
          if (!cancelled) {
            const wait = failures ? Math.min(5000 * failures, 20000) : REFRESH_MS[interval] || 10000;
            timer = setTimeout(load, wait);
          }
        });
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [symbol, chartSymbol, interval]);

  // Toggle moving averages without refetching.
  useEffect(() => {
    showMaRef.current = showMa;
    const { mas } = seriesRef.current;
    if (!mas) return;
    MAS.forEach((m, i) => mas[i].setData(showMa ? sma(candlesRef.current, m.len) : []));
  }, [showMa]);

  const shown = legend || lastCandle;
  const up = shown ? shown.close >= shown.open : true;

  return (
    <div className="pt-chart">
      <div className="pt-chart-toolbar">
        {INTERVALS.map((iv) => (
          <button
            key={iv}
            type="button"
            className={iv === interval ? 'pt-iv pt-iv-active' : 'pt-iv'}
            onClick={() => setIntervalKey(iv)}
          >
            {iv}
          </button>
        ))}
        <span className="pt-chart-sep" />
        <button
          type="button"
          className={showMa ? 'pt-iv pt-iv-active' : 'pt-iv'}
          onClick={() => setShowMa((v) => !v)}
          title="Moving averages 7 / 25 / 99"
        >
          MA
        </button>
        {source === 'price-index' && (
          <span className="pt-chart-note" title="No fills in this window - showing the published price index">
            Price index
          </span>
        )}
      </div>

      <div className="pt-chart-legend">
        <strong>{symbol}</strong>
        <span className="pt-muted">· {interval}</span>
        {shown && (
          <>
            <span>
              O <b className={up ? 'pt-up' : 'pt-down'}>{fmtPrice(shown.open)}</b>
            </span>
            <span>
              H <b className={up ? 'pt-up' : 'pt-down'}>{fmtPrice(shown.high)}</b>
            </span>
            <span>
              L <b className={up ? 'pt-up' : 'pt-down'}>{fmtPrice(shown.low)}</b>
            </span>
            <span>
              C <b className={up ? 'pt-up' : 'pt-down'}>{fmtPrice(shown.close)}</b>
            </span>
            <span className={up ? 'pt-up' : 'pt-down'}>
              {shown.open
                ? `${up ? '+' : ''}${(((shown.close - shown.open) / shown.open) * 100).toFixed(2)}%`
                : ''}
            </span>
            {shown.volume != null && <span>Vol {fmtVol(shown.volume)}</span>}
          </>
        )}
        {showMa && (
          <span className="pt-ma-keys">
            {MAS.map((m) => (
              <span key={m.len} style={{ color: m.color }}>
                MA{m.len}
              </span>
            ))}
          </span>
        )}
      </div>

      <div className="pt-chart-canvas" ref={containerRef}>
        {api && (
          <ChartDrawings
            chart={api.chart}
            series={api.candles}
            candlesRef={candlesRef}
            intervalSec={INTERVAL_SEC[interval] || 3600}
            storageKey={`hd_drawings:${symbol || ''}`}
            fmtPrice={fmtPrice}
          />
        )}
        {status !== 'ready' && (
          <div className="pt-chart-overlay">
            {status === 'loading'
              ? 'Loading chart…'
              : status === 'empty'
              ? 'No trades in this period yet'
              : 'Chart data unavailable - retrying'}
          </div>
        )}
      </div>
    </div>
  );
}
